import React, { useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { FormProvider, useForm } from 'react-hook-form';
import { Fade } from '@mui/material';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import {
  endedCallDataAtom,
  openSnackbarAdviseAtom,
  selectedBranchAtom,
  selectedCustomerIdAtom,
  additionalCustomerDataAtom,
  activityTypesAtom,
} from '../../Atoms';
import CustomerSectionPage from './CustomerSectionPage';
import useAPI from '../../api';
import EmailWindow from './EmailSection/EmailWindow';
import { isAroundTime } from '../../Helpers/dates';
import TemplateMessageDialog from '../../Components/Dialog/TemplateMessageDialog';
import SmsAfterCallWrapper from './Shared/SmsAfterCallWrapper';
import groupTemplatesByName from '../../Helpers/Arrays/groupTemplatesByName';
import Chat from './ChatSection';
import useLocationName from '../../Hooks/useLocationName';
import CampaignSelectionDialog from './Shared/CampaignSelectionDialog';
import '../../Styles/app.scss';

const CustomerComponents = function CustomerComponents({
  removeClickedItemToDrawer,
  reAddNotificationToDrawer,
  setUnreadMessagesCounter,
  recordCountDrawer = 0,
  unreadMessagesCounter = 0,
  sidebarOpen = false,
  userRoles = [],
}) {
  const API = useAPI();
  const methods = useForm();
  const intl = useIntl();

  const locationName = useLocationName();
  const [customerId, setCustomerId] = useAtom(selectedCustomerIdAtom);
  const additionalCustomerData = useAtomValue(additionalCustomerDataAtom);
  const selectedBranch = useAtomValue(selectedBranchAtom);
  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);
  const activityTypes = useAtomValue(activityTypesAtom);

  const [campaignId, setCampaignId] = useState(null);
  const [campaignSelectionDialog, setCampaignSelectionDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [contactIndex, setContactIndex] = useState(0);
  const [endedCallData, setEndedCallData] = useAtom(endedCallDataAtom);
  const [chatOpen, setChatOpen] = useState(null);
  const [endCallMessage, setEndCallMessage] = useState(null);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null);
  const [selectedEmailAddress, setSelectedEmailAddress] = useState(null);
  const [customerInformationData, setCustomerInformationData] = useState(null);
  const [emailWindowOpen, setEmailWindowOpen] = useState(null);
  const [sendEndCallMessage, setSendEndCallMessage] = useState(false);
  const [customerUnreadMessages, setCustomerUnreadMessages] = useState(0);
  const [smsSubmit, setSmsSubmit] = useState(false);

  const handleOpenEmailWindow = () => {
    setEmailWindowOpen('SEND');
  };
  const handleReplyMail = (data) => {
    setEmailWindowOpen(data);
  };
  const handleCloseEmailWindow = () => {
    setEmailWindowOpen(null);
  };

  const handleChangeContactIndex = (index) => {
    setContactIndex(index);
  };

  const getCustomer = useCallback(() => {
    if (customerId && (selectedBranch || additionalCustomerData?.branchId))
      //load the customer data
      API.getCustomer(
        customerId,
        additionalCustomerData?.branchId ?? selectedBranch.id,
        (result) => {
          if (result.status === 204) {
            setOpenSnackbarAdvise({
              message: intl.messages.CUSTOMER_NOT_FOUND,
              severity: 'error',
            });
            setCustomerId(null);
            return;
          }
          setSelectedCustomer(result);
          setCustomerInformationData({
            ...result,
            ...(result?.contacts[contactIndex] ?? {}),
            mobilePhoneNumber:
              result?.contacts[contactIndex]?.contactPhones?.find(
                ({ phoneTypeId }) => phoneTypeId === 1
              )?.phoneNumber || '',
            landlinePhoneNumber:
              result?.contacts[contactIndex]?.contactPhones?.find(
                ({ phoneTypeId }) => phoneTypeId === 3
              )?.phoneNumber || '',
            email: result?.contacts[contactIndex]?.contactEmailAddresses?.find(
              ({ emailTypeId }) => emailTypeId === 1
            )?.email,
            ...result?.contacts[contactIndex]?.contactAddresses?.find(
              ({ addressTypeId }) => addressTypeId === 1
            ),
          });
        },
        (err) => {
          console.log(err);
        }
      );
  }, [customerId, selectedBranch, additionalCustomerData]);

  useEffect(() => {
    getCustomer();
  }, [customerId, selectedBranch]);

  useEffect(() => {
    if (selectedCustomer) {
      const activePhones = selectedCustomer.contacts[
        contactIndex
      ].contactPhones?.filter(({ active }) => active);
      setSelectedPhoneNumber(
        (activePhones?.find(({ priority }) => priority) || activePhones?.[0])
          ?.phoneNumber || null
      );
      setSelectedEmailAddress(
        selectedCustomer?.contacts[contactIndex]?.contactEmailAddresses?.find(
          ({ priority }) => priority
        )?.email || null
      );
    }
  }, [selectedCustomer, contactIndex]);

  useEffect(() => {
    if (selectedBranch && customerId) {
      API.getUnreadMessagesCount(
        customerId,
        (result) => {
          setCustomerUnreadMessages(result);
        },
        (err) => {
          console.log(err);
        }
      );
    }
  }, [customerId]);

  const changePhoneOrMail = (value, isPhone, isMobile, isNew) => {
    const updatedCustomer = { ...selectedCustomer };
    if (isPhone) {
      if (isNew) {
        if (value.isPrincipal) {
          if (isMobile) {
            updatedCustomer.mobilePhoneNumber = value.phoneNumber;
          } else {
            updatedCustomer.landlinePhoneNumber = value.phoneNumber;
          }
        }
        updatedCustomer.contacts?.[contactIndex]?.contactPhones.push({
          phoneNumber: value.phoneNumber,
          phoneTypeId: value.phoneTypeId,
          phoneType: value.phoneType,
          active: true,
          priority: value.priority,
        });
      } else {
        const phoneItem = updatedCustomer.contacts?.[
          contactIndex
        ]?.contactPhones.find(({ phoneNumber }) => phoneNumber === value);
        if (isMobile) {
          updatedCustomer.mobilePhoneNumber = phoneItem.active
            ? phoneItem.phoneNumber
            : null;
        } else {
          updatedCustomer.landlinePhoneNumber = phoneItem.active
            ? phoneItem.phoneNumber
            : null;
        }
      }
    } else if (isNew) {
      updatedCustomer.contacts?.[contactIndex]?.contactEmailAddresses.push({
        email: value.email,
        emailTypeId: value.emailTypeId,
        emailType: value.emailType,
        active: true,
        priority: value.priority,
      });
    } else {
      let item = updatedCustomer.contacts?.[
        contactIndex
      ]?.contactEmailAddresses.find(({ email }) => email === value);
      const index = updatedCustomer.contacts?.[
        contactIndex
      ]?.contactEmailAddresses.findIndex(({ email }) => email === value);
      item = Object.assign(item, { active: !item.active });
      updatedCustomer.contacts?.[contactIndex]?.contactEmailAddresses.splice(
        index,
        1,
        item
      );
    }
    setSelectedCustomer(updatedCustomer);
    setCustomerInformationData({
      ...updatedCustomer,
      ...(updatedCustomer?.contacts[contactIndex] ?? {}),
      mobilePhoneNumber:
        updatedCustomer?.contacts[contactIndex]?.contactPhones?.find(
          ({ phoneTypeId }) => phoneTypeId === 1
        )?.phoneNumber || '',
      landlinePhoneNumber:
        updatedCustomer?.contacts[contactIndex]?.contactPhones?.find(
          ({ phoneTypeId }) => phoneTypeId === 3
        )?.phoneNumber || '',
      email: updatedCustomer?.contacts[
        contactIndex
      ]?.contactEmailAddresses?.find(({ emailTypeId }) => emailTypeId === 1)
        ?.email,
      ...updatedCustomer?.contacts[contactIndex]?.contactAddresses?.find(
        ({ addressTypeId }) => addressTypeId === 1
      ),
    });
  };

  const handleSendEmail = (submitData, isSms) => {
    const formData = new FormData();

    formData.append('receiver', submitData?.receiver);
    formData.append('subject', submitData?.subject);
    formData.append('body', submitData?.body);
    formData.append('replyAll', submitData?.replyAll);

    if (submitData?.parentActivityId)
      formData.append('parentActivityId', submitData?.parentActivityId);
    if (selectedBranch?.smtp) formData.append('sender', selectedBranch.smtp);
    if (submitData?.cc && submitData?.cc.length > 0)
      formData.append('cc', submitData?.cc);
    if (submitData?.bcc && submitData?.bcc.length > 0)
      formData.append('bcc', submitData?.bcc);
    if (submitData?.inReplyTo)
      formData.append('inReplyTo', submitData?.inReplyTo);
    if (submitData?.files?.length)
      submitData.files.forEach((file) => formData.append('files', file));

    if (formData.get('sender')) {
      API.sendEmail(
        formData,
        { doNotStringify: true },
        () => {
          setOpenSnackbarAdvise({
            message: intl.messages.SUCCESS_EMAIL,
            severity: 'success',
          });
          handleCloseEmailWindow();
          if (isSms !== null) {
            setEndedCallData(null);
            setEndCallMessage(null);
            setSendEndCallMessage(false);
          }
        },
        () => {
          setOpenSnackbarAdvise({
            message: intl.messages.ERROR_EMAIL,
            severity: 'error',
          });
          setSendEndCallMessage(false);
        }
      );
    }
  };

  useEffect(() => {
    if (endCallMessage) {
      if (
        (endCallMessage?.channel === 'BOTH' ||
          endCallMessage?.channel === 'EMAIL') &&
        endCallMessage?.emailBody
      ) {
        if (selectedBranch?.smtp && endCallMessage?.emailBody)
          handleSendEmail(
            {
              receiver: selectedEmailAddress,
              sender: selectedBranch?.smtp,
              subject: endCallMessage?.emailSubject,
              body: endCallMessage?.emailBody,
              parentActivityId: endedCallData.id,
            },
            endCallMessage?.channel === 'BOTH'
          );
      }
    }
  }, [endCallMessage]);

  const handleOpenChat = (event) => {
    setChatOpen(event.currentTarget);
  };

  const handleCloseChat = () => {
    setChatOpen(null);
  };

  const DialActions = [
    {
      icon: <SmsOutlinedIcon />,
      name: 'SMS',
      onClick: handleOpenChat,
      badgeContent: customerUnreadMessages,
      disabled: false,
    },
    {
      icon: <EmailOutlinedIcon />,
      name: 'Email',
      onClick: handleOpenEmailWindow,
      disabled: false,
    },
  ];

  useEffect(() => {
    if (!customerId) {
      setSelectedCustomer(null); //unmounts customer details
      setContactIndex(0);
    }
  }, [customerId]);

  const checkSubmit = (value) => {
    setSmsSubmit(value);
  };

  const handleOpenCampaignSelectionDialog = () => {
    setCampaignSelectionDialog(true);
  };
  const handleCloseCampaignSelectionDialog = () => {
    setCampaignSelectionDialog(false);
  };

  useEffect(() => {
    if (!selectedCustomer) {
      setChatOpen(null);
      return;
    }
    if (selectedCustomer?.activeCampaigns?.length === 1) {
      setCampaignId(selectedCustomer.activeCampaigns[0].id);
      return;
    }
    if (selectedCustomer?.activeCampaigns?.length > 1) {
      handleOpenCampaignSelectionDialog();
    }
  }, [selectedCustomer]);

  const handleChangeCampaignId = (campaignId) => {
    setCampaignId(campaignId);
    handleCloseCampaignSelectionDialog();
  };

  useEffect(() => {
    if (!additionalCustomerData?.activityTypeId) return;
    if (
      additionalCustomerData?.activityTypeId ===
      activityTypes?.find((type) => type.name === 'SMS')?.id
    ) {
      setChatOpen(true);
    }
  }, [additionalCustomerData]);

  const open = Boolean(chatOpen);
  return (
    <>
      <Fade in={Boolean(customerId)} unmountOnExit>
        <div style={{ width: '100%', height: '100%' }}>
          {selectedCustomer && (
            <>
              <CampaignSelectionDialog
                open={campaignSelectionDialog}
                onClose={handleCloseCampaignSelectionDialog}
                activeCampaigns={selectedCustomer?.activeCampaigns || []}
                onSubmit={handleChangeCampaignId}
                campaignId={campaignId}
              />
              <CustomerSectionPage
                activeCampaigns={selectedCustomer?.activeCampaigns || []}
                campaignId={campaignId}
                handleOpenCampaignSelectionDialog={
                  handleOpenCampaignSelectionDialog
                }
                userRoles={userRoles}
                customerId={customerId}
                openReply={handleReplyMail}
                activityParentId={
                  //if from appointments section and around time
                  additionalCustomerData?.parentId &&
                  additionalCustomerData?.recallTime
                    ? isAroundTime(additionalCustomerData.recallTime, 10)
                      ? additionalCustomerData.parentId
                      : null
                    : //if from priorities section and activitytype is CALL
                      additionalCustomerData?.activityTypeId ===
                        activityTypes?.find((type) => type.name === 'CALL')?.id
                      ? additionalCustomerData?.parentId
                      : null
                }
                selectedPhoneNumber={selectedPhoneNumber}
                setSelectedPhoneNumber={setSelectedPhoneNumber}
                selectedEmailAddress={selectedEmailAddress}
                setSelectedEmailAddress={setSelectedEmailAddress}
                selectedCustomer={selectedCustomer}
                contactIndex={contactIndex}
                handleChangeContactIndex={handleChangeContactIndex}
                customerInformationData={customerInformationData}
                reloadCustomer={getCustomer}
                changePhoneOrMail={changePhoneOrMail}
                actions={DialActions}
                customerUnreadMessages={customerUnreadMessages}
                sidebarOpen={sidebarOpen}
              />

              <FormProvider {...methods}>
                <TemplateMessageDialog
                  id="sms-aftercall"
                  title={intl.messages.NEW_MESSAGE}
                  submitDisabled={!smsSubmit}
                  handleSubmit={() => setSendEndCallMessage(true)}
                  open={
                    endedCallData !== null &&
                    endedCallData?.outcome !== 9 && //wrong number
                    endedCallData?.outcome !== 18 && //inexistent number
                    endedCallData?.messageTemplates?.length > 0 &&
                    selectedPhoneNumber?.substring(0, 4) === '+393'
                  } //only if mobile phone number
                  handleClose={() => {
                    setEndedCallData(null);
                  }}
                  widgetWrapper={
                    <SmsAfterCallWrapper
                      failedChecks={endedCallData?.failedChecks}
                      checkSubmit={checkSubmit}
                      customerId={selectedCustomer?.id || customerId}
                      activityId={endedCallData?.id}
                      setEndCallMessage={setEndCallMessage}
                      selectedBranch={selectedBranch}
                      sendEndCallMessage={sendEndCallMessage}
                      templates={groupTemplatesByName(
                        endedCallData?.messageTemplates
                      )}
                      isEmailAddressAvailable={
                        selectedEmailAddress !== null && locationName !== 'SPOT'
                      }
                      customMessage={locationName === 'SPOT'}
                    />
                  }
                />
              </FormProvider>

              <Chat
                open={open}
                onClose={handleCloseChat}
                customerId={customerId}
                campaignId={campaignId}
                branchId={selectedBranch.id}
                removeClickedItemToDrawer={removeClickedItemToDrawer}
                reAddNotificationToDrawer={reAddNotificationToDrawer}
                setUnreadMessagesCounter={setUnreadMessagesCounter}
                customerUnreadMessages={customerUnreadMessages}
                setCustomerUnreadMessages={setCustomerUnreadMessages}
                recordCountDrawer={recordCountDrawer}
                unreadMessagesCounter={unreadMessagesCounter}
                endCallMessage={endCallMessage}
                setEndCallMessage={setEndCallMessage}
                setSendEndCallMessage={setSendEndCallMessage}
                setEndedCallData={setEndedCallData}
              />
              <EmailWindow
                open={Boolean(emailWindowOpen)}
                replyData={emailWindowOpen === 'SEND' ? null : emailWindowOpen}
                close={handleCloseEmailWindow}
                onSubmit={handleSendEmail}
                defaultEmail={selectedEmailAddress}
              />
            </>
          )}
        </div>
      </Fade>
    </>
  );
};

CustomerComponents.propTypes = {
  removeClickedItemToDrawer: PropTypes.func.isRequired,
  reAddNotificationToDrawer: PropTypes.func.isRequired,
  setUnreadMessagesCounter: PropTypes.func.isRequired,
  recordCountDrawer: PropTypes.number,
  unreadMessagesCounter: PropTypes.number,
};

export default CustomerComponents;
